import type { StatRow } from '../../../Repository/Admin/AdminStatsRepository.js'
import {
  countBy,
  countByLabel,
  histogram,
  monthlySeries,
  numbersOf,
  shortId,
  sumOf,
  summarize,
  takeTop,
  type Datum,
  type Summary
} from './statsAggregations.js'

export type Scope = 'orders' | 'projects'

interface BaseMetric {
  id: string
  title: string
  scope: Scope
}

/** A scalar metric (tile). `monthly`, when present, yields a per-month value for the trend view. */
export interface KpiMetric extends BaseMetric {
  kind: 'kpi'
  value: (rows: StatRow[]) => string
  monthly?: (bucket: StatRow[]) => number
}

/** A chart metric — `bar` for breakdowns/histograms, `line` for time series. */
export interface ChartMetric extends BaseMetric {
  kind: 'bar' | 'line'
  data: (rows: StatRow[]) => Datum[]
  summary?: (rows: StatRow[]) => Summary
  rotateLabels?: boolean
  color?: string
}

export type Metric = KpiMetric | ChartMetric

/** Timestamp column each scope is bucketed by for monthly trends. */
export const metricDateKey = (scope: Scope): string => (scope === 'orders' ? 'created_at' : 'version_at')

const str = (value: unknown): string | undefined => (typeof value === 'string' && value.length > 0 ? value : undefined)
const pctNum = (part: number, total: number): number => (total > 0 ? Math.round((part / total) * 100) : 0)
const isTrue = (key: string) => (row: StatRow): boolean => row[key] === true

// --- builders (keep the registry below terse) ----------------------------------------------------

const pctKpi = (id: string, title: string, scope: Scope, predicate: (row: StatRow) => boolean): KpiMetric => ({
  kind: 'kpi',
  id,
  title,
  scope,
  value: (rows) => (rows.length ? `${pctNum(rows.filter(predicate).length, rows.length)}%` : '–'),
  monthly: (bucket) => pctNum(bucket.filter(predicate).length, bucket.length)
})

const sumKpi = (id: string, title: string, scope: Scope, key: string): KpiMetric => ({
  kind: 'kpi',
  id,
  title,
  scope,
  value: (rows) => String(sumOf(rows, key)),
  monthly: (bucket) => sumOf(bucket, key)
})

const avgKpi = (id: string, title: string, scope: Scope, key: string): KpiMetric => ({
  kind: 'kpi',
  id,
  title,
  scope,
  value: (rows) => summarize(numbersOf(rows, key)).avg.toFixed(1),
  monthly: (bucket) => Number(summarize(numbersOf(bucket, key)).avg.toFixed(1))
})

const countKpi = (id: string, title: string, scope: Scope): KpiMetric => ({
  kind: 'kpi',
  id,
  title,
  scope,
  value: (rows) => String(rows.length),
  monthly: (bucket) => bucket.length
})

const breakdown = (id: string, title: string, scope: Scope, key: string, color?: string, rotate = true): ChartMetric => ({
  kind: 'bar',
  id,
  title,
  scope,
  rotateLabels: rotate,
  color,
  data: (rows) => countBy(rows, key)
})

const distribution = (id: string, title: string, scope: Scope, key: string, color?: string, positiveOnly = false): ChartMetric => {
  const values = (rows: StatRow[]): number[] => numbersOf(rows, key).filter((v) => (positiveOnly ? v > 0 : true))
  return { kind: 'bar', id, title, scope, color, data: (rows) => histogram(values(rows)), summary: (rows) => summarize(values(rows)) }
}

const orgBreakdown = (
  id: string,
  title: string,
  scope: Scope,
  nameKey: string,
  idKey: string,
  fallbackKey: string | undefined,
  color?: string
): ChartMetric => ({
  kind: 'bar',
  id,
  title,
  scope,
  rotateLabels: true,
  color,
  data: (rows) =>
    takeTop(
      countByLabel(rows, (r) => str(r[nameKey]) ?? shortId(r[idKey]) ?? (fallbackKey ? shortId(r[fallbackKey]) : undefined)),
      20
    )
})

const trend = (id: string, title: string, scope: Scope, color?: string): ChartMetric => ({
  kind: 'line',
  id,
  title,
  scope,
  color,
  data: (rows) => monthlySeries(rows, metricDateKey(scope), (bucket) => bucket.length)
})

// --- registry ------------------------------------------------------------------------------------

export const METRICS: Metric[] = [
  // Orders — KPIs
  countKpi('orders-total', 'Orders', 'orders'),
  pctKpi('orders-claimed', 'Claimed', 'orders', isTrue('is_claimed')),
  pctKpi('orders-unclaimed-lab', 'Unclaimed (no lab)', 'orders', isTrue('lab_unclaimed')),
  pctKpi('orders-unclaimed-customer', 'Unclaimed (no customer)', 'orders', isTrue('customer_unclaimed')),
  pctKpi('orders-reached-postproc', 'Reached postproc', 'orders', isTrue('reached_postproc')),
  pctKpi('orders-abandoned', 'Abandoned after preproc', 'orders', isTrue('abandoned_after_preproc')),
  pctKpi('orders-incomplete', 'Incomplete (alive)', 'orders', isTrue('incomplete_not_deleted')),
  pctKpi('orders-deleted', 'Deleted', 'orders', isTrue('is_deleted')),
  pctKpi('orders-report-errors', 'Orders w/ report errors', 'orders', (r) => Number(r.report_error_count) > 0),
  sumKpi('orders-nc-total', 'Σ negative controls', 'orders', 'nc_count'),
  sumKpi('orders-nc-meta', 'NCs with metadata', 'orders', 'nc_with_metadata_count'),
  sumKpi('orders-deleted-samples', 'Σ deleted samples', 'orders', 'deleted_sample_count'),
  sumKpi('orders-deleted-datasets', 'Σ deleted datasets', 'orders', 'deleted_dataset_count'),
  sumKpi('orders-samples-error', 'Σ samples w/ preproc error', 'orders', 'samples_with_error'),
  sumKpi('orders-samples-no-otu', 'Σ samples w/o OTU (postproc)', 'orders', 'samples_without_otu'),
  // Orders — charts
  trend('orders-created-trend', 'Orders created / month', 'orders', '#0ea5e9'),
  breakdown('orders-by-taxon', 'Orders per taxon', 'orders', 'taxon', '#10b981'),
  breakdown('orders-by-processing', 'Orders per processing type', 'orders', 'processing_type', '#6366f1', false),
  breakdown('orders-by-region', 'Orders per target region', 'orders', 'region', '#14b8a6'),
  breakdown('orders-by-sampleconfig', 'Orders per sample configuration', 'orders', 'sample_configuration', '#8b5cf6'),
  breakdown('orders-by-redlist', 'Orders per redlist mode', 'orders', 'redlist_mode', '#f43f5e'),
  breakdown('orders-by-type', 'Orders per type', 'orders', 'order_type', '#64748b', false),
  orgBreakdown('orders-by-customer', 'Orders per customer org (top 20)', 'orders', 'customer_org_name', 'customer_org', undefined, '#0d9488'),
  orgBreakdown('orders-by-lab', 'Orders per lab org (top 20)', 'orders', 'lab_org_name', 'lab_org', undefined, '#7c3aed'),
  distribution('orders-samples', 'Samples per order', 'orders', 'sample_count'),
  distribution('orders-datasets', 'Datasets per order', 'orders', 'dataset_count', '#f59e0b'),
  distribution('orders-reports', 'Reports per order (≥1)', 'orders', 'report_count', '#ec4899', true),
  distribution('orders-preproc', 'Preprocessing attempts per order', 'orders', 'preproc_attempts', '#a855f7'),
  distribution('orders-postproc', 'Postprocessing attempts per order', 'orders', 'postproc_attempts', '#d946ef'),

  // Projects — KPIs
  countKpi('projects-total', 'Projects', 'projects'),
  pctKpi('projects-draft', 'Draft', 'projects', isTrue('is_draft')),
  avgKpi('projects-avg-members', 'Avg members', 'projects', 'member_count'),
  avgKpi('projects-avg-editors', 'Avg editors', 'projects', 'editor_count'),
  avgKpi('projects-avg-samples', 'Avg samples', 'projects', 'sample_count'),
  avgKpi('projects-avg-notes', 'Avg notes', 'projects', 'note_count'),
  // Projects — charts
  trend('projects-created-trend', 'Projects created / month', 'projects', '#0ea5e9'),
  distribution('projects-members', 'Members per project', 'projects', 'member_count', '#10b981'),
  distribution('projects-editors', 'Editors per project', 'projects', 'editor_count', '#22c55e'),
  distribution('projects-samples', 'Samples per project', 'projects', 'sample_count'),
  distribution('projects-order-span', 'Orders spanned per project', 'projects', 'order_span', '#f59e0b'),
  distribution('projects-notes', 'Notes per project', 'projects', 'note_count', '#ec4899'),
  breakdown('projects-by-taxon', 'Projects per taxon', 'projects', 'taxon', '#6366f1'),
  orgBreakdown('projects-by-owner', 'Projects per owner (top 20)', 'projects', 'owner_org_name', 'owner_org', 'owner_user', '#0d9488')
]

export const metricById = (id: string | undefined): Metric | undefined => METRICS.find((m) => m.id === id)
